import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { apiService } from '../../services/api';

interface JobSchedule {
  name: string;
  description: string;
  schedule: string;
  enabled: boolean;
  last_run: string | null;
  next_run: string | null;
}

interface JobSchedules {
  [key: string]: JobSchedule;
}

const JobSchedulerTab: React.FC = () => {
  const [jobSchedules, setJobSchedules] = useState<JobSchedules>({});
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    jobId: string;
    schedule: string;
    enabled: boolean;
  }>({
    open: false,
    jobId: '',
    schedule: '',
    enabled: true,
  });
  const [triggering, setTriggering] = useState<string | null>(null);

  useEffect(() => {
    loadJobSchedules();
  }, []);

  const loadJobSchedules = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<JobSchedules>('/admin/job-schedules');
      setJobSchedules(response);
    } catch (error) {
      console.error('Failed to load job schedules:', error);
      toast.error('Failed to load job schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleJob = async (jobId: string, enabled: boolean) => {
    try {
      const response = await apiService.put<JobSchedule>(`/admin/job-schedules/${jobId}`, {
        enabled: !enabled,
      });
      
      setJobSchedules(prev => ({
        ...prev,
        [jobId]: response,
      }));
      
      toast.success(`Job ${!enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Failed to toggle job:', error);
      toast.error('Failed to update job status');
    }
  };

  const handleEditSchedule = (jobId: string, currentSchedule: string, enabled: boolean) => {
    setEditDialog({
      open: true,
      jobId,
      schedule: currentSchedule,
      enabled,
    });
  };

  const handleSaveSchedule = async () => {
    try {
      const response = await apiService.put<JobSchedule>(`/admin/job-schedules/${editDialog.jobId}`, {
        schedule: editDialog.schedule,
        enabled: editDialog.enabled,
      });
      
      setJobSchedules(prev => ({
        ...prev,
        [editDialog.jobId]: response,
      }));
      
      setEditDialog({ open: false, jobId: '', schedule: '', enabled: true });
      toast.success('Job schedule updated successfully');
    } catch (error: any) {
      console.error('Failed to update schedule:', error);
      toast.error(error.response?.data?.error || 'Failed to update job schedule');
    }
  };

  const handleTriggerJob = async (jobId: string) => {
    try {
      setTriggering(jobId);
      const response = await apiService.post<{ message: string }>(`/admin/job-schedules/${jobId}/trigger`);
      toast.success(response.message);
      
      // Reload schedules to get updated last_run time
      await loadJobSchedules();
    } catch (error: any) {
      console.error('Failed to trigger job:', error);
      toast.error(error.response?.data?.error || 'Failed to trigger job');
    } finally {
      setTriggering(null);
    }
  };

  const handleFixStaleData = async () => {
    try {
      setTriggering('fix_stale_data');
      const response = await apiService.post<{ message: string }>('/admin/fix-stale-data');
      toast.success(response.message);
      toast.info('This may take several minutes. Check background jobs status for progress.');
    } catch (error: any) {
      console.error('Failed to fix stale data:', error);
      toast.error(error.response?.data?.error || 'Failed to fix stale data');
    } finally {
      setTriggering(null);
    }
  };

  const handleRefreshDayChanges = async () => {
    try {
      setTriggering('refresh_day_changes');
      const response = await apiService.post<{ message: string }>('/admin/refresh-day-changes');
      toast.success('Day price changes refreshed successfully');
    } catch (error: any) {
      console.error('Failed to refresh day changes:', error);
      toast.error(error.response?.data?.error || 'Failed to refresh day changes');
    } finally {
      setTriggering(null);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getJobStatusColor = (enabled: boolean, lastRun: string | null) => {
    if (!enabled) return 'default';
    if (!lastRun) return 'warning';
    return 'success';
  };

  const getJobStatusText = (enabled: boolean, lastRun: string | null) => {
    if (!enabled) return 'Disabled';
    if (!lastRun) return 'Never Run';
    return 'Active';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ScheduleIcon fontSize="small" />
        Background Job Scheduler
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2, py: 0.5, '& .MuiAlert-message': { py: 0.5 } }}>
        <Typography variant="caption">
          Configure the frequency and timing of background jobs. Use cron expressions (minute hour day month weekday).
          Examples: "*/15 9-16 * * 1-5" = every 15 minutes, 9-16 hours, Mon-Fri
        </Typography>
      </Alert>

      {/* Quick Actions */}
      <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Actions
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<PlayIcon fontSize="small" />}
            onClick={() => handleFixStaleData()}
            disabled={triggering === 'fix_stale_data'}
          >
            {triggering === 'fix_stale_data' ? (
              <>
                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                Fixing...
              </>
            ) : (
              'Fix Stale Price Data'
            )}
          </Button>
          
          <Button
            variant="outlined"
            color="info"
            size="small"
            startIcon={<CheckIcon fontSize="small" />}
            onClick={() => handleRefreshDayChanges()}
            disabled={triggering === 'refresh_day_changes'}
          >
            {triggering === 'refresh_day_changes' ? (
              <>
                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                Refreshing...
              </>
            ) : (
              'Refresh Day Changes'
            )}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        {Object.entries(jobSchedules).map(([jobId, job]) => (
          <Grid item xs={12} sm={6} md={4} key={jobId}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {job.name}
                  </Typography>
                  <Chip
                    label={getJobStatusText(job.enabled, job.last_run)}
                    color={getJobStatusColor(job.enabled, job.last_run)}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.3 }}>
                  {job.description}
                </Typography>
                
                <Box mb={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Schedule (Cron):
                  </Typography>
                  <Typography variant="caption" fontFamily="monospace" bgcolor="grey.100" px={0.75} py={0.25} borderRadius={0.5} display="block" sx={{ fontSize: '0.75rem' }}>
                    {job.schedule}
                  </Typography>
                </Box>
                
                <Box display="flex" gap={2} mb={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Last Run: {formatDateTime(job.last_run)}
                  </Typography>
                </Box>
                
                <Box display="flex" gap={2} mb={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Next Run: {formatDateTime(job.next_run)}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <Typography variant="caption" mr={0.5} sx={{ fontSize: '0.75rem' }}>
                      Enabled:
                    </Typography>
                    <Switch
                      checked={job.enabled}
                      onChange={() => handleToggleJob(jobId, job.enabled)}
                      size="small"
                      sx={{ transform: 'scale(0.8)' }}
                    />
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title="Edit Schedule">
                      <IconButton
                        size="small"
                        onClick={() => handleEditSchedule(jobId, job.schedule, job.enabled)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Button
                      size="small"
                      variant="text"
                      startIcon={
                        triggering === jobId ? (
                          <CircularProgress size={12} />
                        ) : (
                          <PlayIcon fontSize="small" />
                        )
                      }
                      onClick={() => handleTriggerJob(jobId)}
                      disabled={triggering === jobId}
                      sx={{ fontSize: '0.75rem', py: 0.25, minWidth: 'auto' }}
                    >
                      Trigger Now
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Schedule Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ ...editDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Job Schedule</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              label="Cron Schedule"
              value={editDialog.schedule}
              onChange={(e) => setEditDialog({ ...editDialog, schedule: e.target.value })}
              placeholder="*/15 9-16 * * 1-5"
              helperText="Format: minute hour day month weekday (use * for any, ranges like 9-16, lists like 1,3,5)"
              margin="normal"
            />
            
            <Box mt={2} display="flex" alignItems="center">
              <Typography variant="body2" mr={2}>
                Enabled:
              </Typography>
              <Switch
                checked={editDialog.enabled}
                onChange={(e) => setEditDialog({ ...editDialog, enabled: e.target.checked })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ ...editDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={handleSaveSchedule} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobSchedulerTab;
